import { FC, createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'
import type { MessagePayload } from 'firebase/messaging'
import {
  CategoriaNotificacaoType,
  QUERIES,
  WithChildren,
  showAlert,
  showError,
  stringifyRequestQuery,
} from '../../../../../_metronic/helpers'
import { useAuth } from '../../../auth'
import { useToast } from '../../../../components'
import {
  getNotificacoes,
  getNotificacoesNaoLidasCount,
  marcarNotificacaoComoLida,
  marcarTodasNotificacoesComoLidas,
} from './_requests'
import { NotificacaoList } from './_models'
import { AbrirNotificacao, useAbrirNotificacao } from './useAbrirNotificacao'
import { NotificacaoToastContent } from '../components/NotificacaoToastContent'
import { usePushNotifications } from '../push/usePushNotifications'
import { MensagemPush, lerMensagemPush } from '../push/lerMensagemPush'
import { revogarPushToken } from '../push/_requests'
import { pedirPermissaoNotificacao, permissaoNotificacao } from '../push/getPushFid'
import {
  getPushOptOut,
  getStoredPushToken,
  setPushOptOut,
  setStoredPushToken,
} from '../push/pushTokenStorage'

type NotificacoesContextProps = {
  notificacoes: NotificacaoList[]
  naoLidas: number
  isLoading: boolean
  refetch: () => void
  marcarComoLida: (id: NotificacaoList['id']) => Promise<void>
  marcarTodasComoLidas: () => Promise<void>
  abrirNotificacao: AbrirNotificacao
  /** Preferência local ligada e permissão do navegador concedida. */
  pushEnabled: boolean
  isTogglingPush: boolean
  togglePush: () => Promise<void>
}

const NotificacoesContext = createContext<NotificacoesContextProps>({
  notificacoes: [],
  naoLidas: 0,
  isLoading: false,
  refetch: () => {},
  marcarComoLida: async () => {},
  marcarTodasComoLidas: async () => {},
  abrirNotificacao: async () => {},
  pushEnabled: true,
  isTogglingPush: false,
  togglePush: async () => {},
})

const query = stringifyRequestQuery({
  page: 0,
  size: 20,
  filters: [{ field: 'lida', operator: '=', value: false }],
})

const JANELA_AGRUPAMENTO_MS = 1500

export const NotificacoesProvider: FC<WithChildren> = ({ children }) => {
  const { auth } = useAuth()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { showToast } = useToast()

  const [pushEnabled, setPushEnabled] = useState(() => !getPushOptOut())
  const [permissao, setPermissao] = useState(permissaoNotificacao)
  const [isTogglingPush, setIsTogglingPush] = useState(false)

  const pushAtivo = pushEnabled && permissao === 'granted'

  const togglePush = useCallback(async () => {
    if (!pushAtivo) {
      const resultado = await pedirPermissaoNotificacao()
      setPermissao(resultado)

      if (resultado === undefined) {
        showAlert({
          title: 'Notificações indisponíveis',
          text: 'Este navegador não recebe notificações push. No iPhone, adicione o LauduzOne à Tela de Início (Compartilhar → Adicionar à Tela de Início) e abra por lá.',
          icon: 'info',
        })
        return
      }
      if (resultado !== 'granted') {
        showAlert({
          title: 'Notificações bloqueadas',
          text: 'As notificações estão bloqueadas para este site. Libere nas configurações do navegador e tente de novo.',
          icon: 'info',
        })
        return
      }

      setPushOptOut(false)
      setPushEnabled(true)
      return
    }

    setIsTogglingPush(true)
    try {
      const token = getStoredPushToken()
      if (token) {
        try {
          await revogarPushToken(token)
        } catch (error) {
          showError(error)
          return
        }
      }
      setStoredPushToken(null)
      setPushOptOut(true)
      setPushEnabled(false)
    } finally {
      setIsTogglingPush(false)
    }
  }, [pushAtivo])

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries([QUERIES.NOTIFICACOES_LIST])
    queryClient.invalidateQueries([QUERIES.NOTIFICACOES_NAO_LIDAS_COUNT])
  }, [queryClient])

  const marcarComoLida = useCallback(
    async (id: NotificacaoList['id']) => {
      if (!id) return
      try {
        await marcarNotificacaoComoLida(id)
        invalidate()
      } catch (error) {
        showError(error)
      }
    },
    [invalidate]
  )

  const abrirNotificacao = useAbrirNotificacao(marcarComoLida)

  const notificarRecebimento = useCallback(
    (dados: {
      id?: number
      titulo: string
      texto?: string
      categoria?: CategoriaNotificacaoType
    }) => {
      const { id } = dados

      showToast({
        content: (
          <NotificacaoToastContent
            titulo={dados.titulo}
            texto={dados.texto}
            categoria={dados.categoria}
          />
        ),
        onClick: () => {
          if (id !== undefined) {
            abrirNotificacao({ id, titulo: dados.titulo })
          } else {
            navigate('/apps/notificacoes')
          }
        },
        duration: 8000,
      })
    },
    [showToast, navigate, abrirNotificacao]
  )

  // Rajada de mensagens (FCM entregando o acumulado) vira um toast só.
  const mensagensPendentes = useRef<MensagemPush[]>([])
  const timerAgrupamento = useRef<ReturnType<typeof setTimeout>>()

  useEffect(
    () => () => {
      if (timerAgrupamento.current) clearTimeout(timerAgrupamento.current)
    },
    []
  )

  const exibirMensagensPendentes = useCallback(() => {
    const mensagens = mensagensPendentes.current
    mensagensPendentes.current = []
    timerAgrupamento.current = undefined

    invalidate()

    if (mensagens.length === 1) {
      notificarRecebimento(mensagens[0])
    } else if (mensagens.length > 1) {
      showToast({
        content: (
          <NotificacaoToastContent
            titulo={`Você tem ${mensagens.length} novas notificações`}
            acao='Ver notificações'
          />
        ),
        onClick: () => navigate('/apps/notificacoes'),
        duration: 8000,
      })
    }
  }, [invalidate, notificarRecebimento, showToast, navigate])

  const handleMensagemPush = useCallback(
    (payload: MessagePayload) => {
      const mensagem = lerMensagemPush(payload)
      if (mensagem) mensagensPendentes.current.push(mensagem)

      if (!timerAgrupamento.current) {
        timerAgrupamento.current = setTimeout(exibirMensagensPendentes, JANELA_AGRUPAMENTO_MS)
      }
    },
    [exibirMensagensPendentes]
  )

  usePushNotifications(handleMensagemPush, pushAtivo)

  const {
    isFetching,
    refetch,
    data: response,
  } = useQuery([QUERIES.NOTIFICACOES_LIST, query], () => getNotificacoes(query), {
    enabled: !!auth?.accessToken,
    cacheTime: 0,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  })

  const { data: naoLidasCount } = useQuery(
    [QUERIES.NOTIFICACOES_NAO_LIDAS_COUNT],
    getNotificacoesNaoLidasCount,
    {
      enabled: !!auth?.accessToken,
      cacheTime: 0,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    }
  )

  const marcarTodasComoLidas = useCallback(async () => {
    const ids = (response?.data || []).map((n) => n.id).filter((id): id is number => !!id)
    if (!ids.length) return

    try {
      await marcarTodasNotificacoesComoLidas({ notificacoes: ids })
      invalidate()
    } catch (error) {
      showError(error)
    }
  }, [response?.data, invalidate])

  return (
    <NotificacoesContext.Provider
      value={{
        notificacoes: response?.data || [],
        naoLidas: naoLidasCount?.total || 0,
        isLoading: isFetching,
        refetch,
        marcarComoLida,
        marcarTodasComoLidas,
        abrirNotificacao,
        pushEnabled: pushAtivo,
        isTogglingPush,
        togglePush,
      }}
    >
      {children}
    </NotificacoesContext.Provider>
  )
}

export const useNotificacoes = () => useContext(NotificacoesContext)
